import { useState, useEffect, useCallback } from 'react';
import { useClient } from '../contexts/ClientContext';
import { API_URL } from '../config/api';

export const useApi = () => {
  const { isAdmin } = useClient();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const makeRequest = useCallback(async (endpoint, options = {}) => {
    setLoading(true);
    setError(null);

    try {
      const url = `${API_URL}/api${endpoint}`;
      const headers = {
        'Content-Type': 'application/json',
        ...options.headers
      };

      const response = await fetch(url, {
        ...options,
        headers,
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Métodos específicos para diferentes tipos de datos
  const api = {
    // Campañas
    getCampaigns: () => makeRequest('/campaigns'),
    getCampaign: (id) => makeRequest(`/campaigns/${id}`),
    createCampaign: (data) => makeRequest('/campaigns', {
      method: 'POST',
      body: JSON.stringify(data)
    }),
    updateCampaign: (id, data) => makeRequest(`/campaigns/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    }),
    deleteCampaign: (id) => makeRequest(`/campaigns/${id}`, {
      method: 'DELETE'
    }),

    // Conversaciones
    getConversations: (params = {}) => {
      const queryParams = new URLSearchParams(params).toString();
      return makeRequest(`/conversations${queryParams ? `?${queryParams}` : ''}`);
    },
    getConversation: (id) => makeRequest(`/conversations/${id}`),

    // Reportes OSINT
    getOSINTReports: (params = {}) => {
      const queryParams = new URLSearchParams(params).toString();
      return makeRequest(`/osint_reports${queryParams ? `?${queryParams}` : ''}`);
    },
    getOSINTReport: (id) => makeRequest(`/osint_reports/${id}`),

    // Analytics
    getAnalytics: (params = {}) => {
      const queryParams = new URLSearchParams(params).toString();
      return makeRequest(`/analytics${queryParams ? `?${queryParams}` : ''}`);
    },
    getAnalyticsMetrics: (metricType) => {
      return makeRequest(`/analytics/metrics?type=${metricType}`);
    },

    // Clientes (solo para admins)
    getClients: () => makeRequest('/clients'),
    getClient: (id) => makeRequest(`/clients/${id}`),
    updateClient: (id, data) => makeRequest(`/clients/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    }),

    // Usuarios (solo para admins)
    getUsers: () => makeRequest('/users'),
    getUser: (id) => makeRequest(`/users/${id}`),
    createUser: (data) => makeRequest('/users', {
      method: 'POST',
      body: JSON.stringify(data)
    }),
    updateUser: (id, data) => makeRequest(`/users/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    }),

    // Método genérico para otras llamadas
    request: makeRequest
  };

  return {
    ...api,
    loading,
    error,
    clearError: () => setError(null)
  };
};

// ===================================
// Menú deDatos Analytics generales al inicio
// ===================================
export const useAnalytics = (metricType = 'outbound', options = { enablePolling: true }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const url = `${API_URL}/api/analytics?category=${metricType}`;
      const response = await fetch(url, {
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const analyticsData = await response.json();
      setData(analyticsData);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [metricType]);

  const enablePolling = (options && typeof options.enablePolling === 'boolean') ? options.enablePolling : true;

  useEffect(() => {
    fetchAnalytics();
    let interval;
    if (enablePolling) {
      interval = setInterval(fetchAnalytics, 120000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [fetchAnalytics, enablePolling]);

  return {
    data,
    loading,
    error,
    refetch: fetchAnalytics
  };
};


// ===================================
// Analíticas de Agente IA (ElevenLabs)
// ===================================
export const useAgentAnalytics = (options = { enablePolling: false, enabled: true, startDate: null, endDate: null }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchAgentAnalytics = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Construir URL con parámetros de fecha si se proporcionan
      const params = new URLSearchParams();
      if (options.startDate) {
        params.append('start_date', options.startDate);
      }
      if (options.endDate) {
        params.append('end_date', options.endDate);
      }

      const url = `${API_URL}/api/agent-analytics${params.toString() ? '?' + params.toString() : ''}`;
      const response = await fetch(url, {
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include'
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const payload = await response.json();
      setData(payload);
    } catch (err) {
      setError(err.message);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [options.startDate, options.endDate]);

  const enablePolling = (options && typeof options.enablePolling === 'boolean') ? options.enablePolling : false;
  const enabled = (options && typeof options.enabled === 'boolean') ? options.enabled : true;

  useEffect(() => {
    if (!enabled) {
      console.log('Petición de analíticas agente IA deshabilitada')
      return; // no iniciar si está deshabilitado
    }
    fetchAgentAnalytics();
    let interval;
    if (enablePolling && enabled) {
      interval = setInterval(fetchAgentAnalytics, 120000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [fetchAgentAnalytics, enablePolling, enabled, options.startDate, options.endDate]);

  return { data, loading, error, refetch: fetchAgentAnalytics };
};

// ===================================
// Menú de Distribución de resultados de llamadas
// ===================================

// Hook unificado para estadísticas de GoContact (una sola llamada): expone TMA y TMO
export const useGoContactStats = (metricType = 'outbound') => {
  const [data, setData] = useState(null);
  const [occupancy, setOccupancy] = useState(null);
  const [answered, setAnswered] = useState(null);
  const [abandoned, setAbandoned] = useState(null);
  const [tma, setTma] = useState(null);
  const [tme, setTme] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const metricTypeNormalized = (metricType || '').toLowerCase() === 'inbound' ? 'inbound' : 'outbound';
      const response = await fetch(`${API_URL}/api/gocontact/stats?direction=${encodeURIComponent(metricTypeNormalized)}`, {
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include'
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err?.detail || `HTTP error! status: ${response.status}`);
      }

      const payload = await response.json();
      setData(payload);

      setOccupancy(payload?.occupancy ?? null);
      setAnswered(payload?.answered ?? null);
      setAbandoned(payload?.abandoned ?? null);
      setTma(payload?.tma ?? null);
      setTme(payload?.tme ?? null);
    } catch (err) {
      setError(err.message);
      setData(null);
      setTma(null);
      setTme(null);
    } finally {
      setLoading(false);
    }
  }, [metricType]);

  // El polling se controlará desde el componente
  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  return { data, occupancy, answered, abandoned, tma, tme, loading, error, refetch: fetchStats };
};

// Hook para datos de campañas con filtrado automático
export const useCampaigns = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchCampaigns = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const url = `${API_URL}/api/campaigns`;
      const response = await fetch(url, {
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const campaignsData = await response.json();
      setCampaigns(campaignsData);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCampaigns();

    // Polling cada 30 segundos
    const interval = setInterval(fetchCampaigns, 30000);
    return () => clearInterval(interval);
  }, [fetchCampaigns]);

  return {
    campaigns,
    loading,
    error,
    refetch: fetchCampaigns
  };
};

// Hook para conversaciones con filtrado automático
export const useConversations = (params = {}) => {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchConversations = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const queryParams = new URLSearchParams(params).toString();
      const url = `${API_URL}/api/conversations${queryParams ? `?${queryParams}` : ''}`;
      const response = await fetch(url, {
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const conversationsData = await response.json();
      setConversations(conversationsData);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => {
    fetchConversations();

    // Polling cada 30 segundos
    const interval = setInterval(fetchConversations, 30000);
    return () => clearInterval(interval);
  }, [fetchConversations]);

  return {
    conversations,
    loading,
    error,
    refetch: fetchConversations
  };
};

// Hook para Plan de Calidad
export const useQualityData = (options = { startDate: null, endDate: null, status: null }) => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (options.startDate) params.append('start_date', options.startDate);
      if (options.endDate) params.append('end_date', options.endDate);
      if (options.status) params.append('status', options.status);

      const queryParams = params.toString() ? `?${params.toString()}` : '';

      // Fetch only tasks (stats table was removed)
      const tasksRes = await fetch(`${API_URL}/api/quality/tasks${queryParams}`, { credentials: 'include' });

      if (!tasksRes.ok) {
        let msg = `Error ${tasksRes.status}`;
        try {
          const body = await tasksRes.json();
          if (body.detail) msg = typeof body.detail === 'string' ? body.detail : (body.detail.msg || body.detail.message || JSON.stringify(body.detail));
        } catch (_) {
          const text = await tasksRes.text();
          if (text) msg = text.slice(0, 200);
        }
        throw new Error(msg);
      }

      const tasksData = await tasksRes.json();
      setTasks(Array.isArray(tasksData) ? tasksData : []);

    } catch (err) {
      console.error("Error fetching quality data:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [options.startDate, options.endDate, options.status]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { tasks, loading, error, refetch: fetchData };
};
